import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { IconBookmark, IconTrash } from '../icons';
import { getSavedSearches, saveSearch, deleteSavedSearch } from '../storage';
import { encodeFindToken, decodeToken, findTokenToQuery } from '../links';
import { isEmpty, linkedRuns, normalizeQuery, queryParts } from './queryOps';
import { cutLabel, fmtDateLong } from './util';
import { undoToast } from './toasts';
import './search.css';

const info = (msg) => toast(msg, { type: toast.TYPE.INFO, autoClose: 2500 });
const fail = (msg) => toast(msg, { type: toast.TYPE.ERROR });

// "Scarlet Begonias into Fire on the Mountain, Morning Dew" (max 60 chars).
// Filter-only searches use the header chip labels instead.
export const searchLabel = (q, meta) => {
  const n = normalizeQuery(q);
  const words = [];
  if (n.songs.length) {
    const runAt = {};
    linkedRuns(n).forEach((x) => {
      runAt[x.start] = x;
    });
    let i = 0;
    while (i < n.songs.length) {
      const r = runAt[i];
      if (r) {
        words.push(n.songs.slice(r.start, r.end + 1).map((s) => s.name).join(' into '));
        i = r.end + 1;
      } else {
        words.push(n.songs[i].name);
        i += 1;
      }
    }
  } else {
    queryParts(n, meta).forEach((p) => words.push(p.label));
  }
  return cutLabel(words.join(', ') || 'Saved search', 60);
};

const savedOn = (ms) => {
  const d = new Date(typeof ms === 'number' ? ms : NaN);
  return Number.isFinite(d.getTime()) ? fmtDateLong(d.toISOString().slice(0, 10)) : '';
};

const bandKeyOf = (band) => (band && typeof band === 'object' ? band.key : band);

// Save the current search on this device and load or delete saved ones.
// Entries live in storage.js (localStorage, never throws); a browser that
// will not keep them gets a toast and a list that lasts for this visit.
// Optional (additive): `catalog` ({ songs, venues, cities }) so loaded chips
// get their names; `meta` for era and vibe labels.
export default function SavedSearches({ band, current, onLoad, catalog, meta }) {
  const bandKey = bandKeyOf(band);
  const [all, setAll] = useState(() => getSavedSearches());
  const list = useMemo(() => all.filter((e) => e.bandKey === bandKey), [all, bandKey]);
  const token = useMemo(() => (bandKey && !isEmpty(current) ? encodeFindToken(bandKey, current) : null), [bandKey, current]);
  const savedAlready = !!token && list.some((e) => e.token === token);

  useEffect(() => {
    setAll(getSavedSearches());
    const onStorage = (e) => {
      if (!e || !e.key || e.key === 'ds-saved-searches-v1') setAll(getSavedSearches());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [bandKey]);

  const save = () => {
    if (!bandKey || isEmpty(current)) return;
    if (!token) {
      fail('This search is too long to save.');
      return;
    }
    const next = saveSearch({ bandKey, label: searchLabel(current, meta), token });
    if (!next) {
      fail('Could not save this search.');
      return;
    }
    const entry = next[0];
    setAll((prev) => [entry].concat(prev.filter((e) => e.id !== entry.id && !(e.bandKey === bandKey && e.token === token))));
    const kept = getSavedSearches().some((e) => e.bandKey === bandKey && e.token === token);
    if (kept) info('Search saved');
    else fail('This browser is not keeping saved searches, so this one lasts until you leave the page.');
  };

  const remove = (entry) => {
    deleteSavedSearch(entry.id);
    setAll((prev) => prev.filter((e) => e.id !== entry.id));
    const undo = () => {
      saveSearch(entry);
      setAll((prev) => [entry].concat(prev.filter((e) => e.id !== entry.id)).sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0)));
    };
    undoToast('Saved search removed', undo);
  };

  const load = (entry) => {
    const t = decodeToken(entry.token);
    if (!t || t.kind !== 'find') {
      fail('This saved search could not be opened.');
      return;
    }
    onLoad && onLoad(findTokenToQuery(t.data, catalog || {}));
  };

  return (
    <section className="ss-saved" aria-label="Saved searches">
      <div className="ss-field-head">
        <span className="ss-label">Saved searches</span>
        <button
          type="button"
          className={'ss-textbtn' + (savedAlready ? ' on' : '')}
          onClick={save}
          disabled={isEmpty(current) || savedAlready}
          title={savedAlready ? 'This search is saved' : 'Save this search on this device'}
        >
          <IconBookmark size={14} />
          <span>{savedAlready ? 'Saved' : 'Save'}</span>
        </button>
      </div>
      {list.length === 0 ? (
        <div className="ss-note">Saved searches stay in this browser.</div>
      ) : (
        <ul className="ss-saved-list">
          {list.map((e) => (
            <li key={e.id} className={'ss-saved-row' + (token && e.token === token ? ' current' : '')}>
              <button type="button" className="ss-saved-load" onClick={() => load(e)} title="Load this search">
                <span className="ss-saved-label">{e.label}</span>
                {savedOn(e.savedAt) && <span className="ss-saved-date">Saved {savedOn(e.savedAt)}</span>}
              </button>
              <button type="button" className="icon-btn ss-mini" onClick={() => remove(e)} title="Delete saved search" aria-label={'Delete ' + e.label}>
                <IconTrash size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
